import { create } from 'zustand';
import { getApiClient, setTokens, clearTokens, getTokens, refreshTokens } from '../lib/axios';
import { queryClient } from '../lib/queryClient';
import { useBillingGuardStore } from './billingGuardStore';
import { clearRememberedLocations, useUiStore } from './uiStore';
import { platformApi } from '../api/platformApi';
import type { SupportSessionInfo } from '@commerce-os/shared-types';
import {
  isMfaChallengeSignalled,
  isMfaRequiredResponse,
  type LoginApiResponse,
  type LoginResponse,
  type MeResponse,
} from '../api/authApi';

export interface AuthUser {
  id: string;
  orgId: string;
  email: string;
  firstName: string;
  lastName: string;
  /** Flat role code strings derived from the scoped roles returned by /auth/me */
  roles: string[];
  permissions: string[];
  /** Set while a platform admin works inside a merchant org (`orgId` is
   *  then the merchant's). Optional so fixtures that predate it stay valid. */
  supportSession?: SupportSessionInfo | null;
}

export interface RegisterInput {
  orgName: string;
  email: string;
  password: string;
  firstName: string;
  lastName: string;
  phone?: string;
  /**
   * Phase 36.4 — magic-link signup token from the operator-minted email
   * (reduced 36.2). When present, AuthService.register validates +
   * atomically redeems the token before creating the org/user. Absent
   * when the user reaches /register via the open-signup path.
   */
  signupToken?: string;
}

/**
 * Phase 45.1b — `login` resolves (rather than throws) when the backend
 * answers 403 `SSO_REQUIRED` after a correct password, mirroring the
 * `mfaRequired` flow. The caller redirects the browser to the SSO start
 * URL built from `connectionId`.
 */
export interface LoginResult {
  mfaRequired: boolean;
  ssoRequired?: boolean;
  connectionId?: string;
}

interface AuthState {
  user: AuthUser | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  mfaSessionToken: string | null;

  login: (email: string, password: string) => Promise<LoginResult>;
  register: (input: RegisterInput) => Promise<void>;
  validateMfa: (code: string) => Promise<void>;
  completeSso: (code: string) => Promise<void>;
  logout: () => Promise<void>;
  loadUser: () => Promise<void>;
  switchLocation: (locationId: string) => Promise<boolean>;
  setMfaToken: (token: string) => void;
  clearMfaToken: () => void;
  /** Platform admin: enter a merchant org as CommerceOS Admin. */
  enterOrg: (orgId: string) => Promise<void>;
  /** Platform admin: leave the merchant org, back to CommerceOS. */
  exitOrg: () => Promise<void>;
  /** A silent refresh landed in another org (a support session ended or
   *  ran out): re-hydrate for it. */
  syncOrgAfterRefresh: () => Promise<void>;
}

/**
 * Monotonic ticker used by `switchLocation` to discard results from
 * superseded /auth/refresh requests. Kept in module scope so rapid
 * LocationSwitcher clicks can't produce out-of-order token updates.
 */
let switchLocationSeq = 0;

/** Normalize the /auth/me response into a flat AuthUser shape */
function normalizeUser(me: MeResponse): AuthUser {
  return {
    id: me.id,
    orgId: me.orgId,
    email: me.email,
    firstName: me.firstName,
    lastName: me.lastName,
    roles: me.roles.map((r) => r.code),
    permissions: me.permissions ?? [],
    supportSession: me.supportSession ?? null,
  };
}

/**
 * Adopt tokens for a DIFFERENT org — a platform admin entering or leaving a
 * merchant org. The selected location belongs to the org being left: clear
 * it FIRST, because every silent refresh re-sends it and the API refuses a
 * location outside the token's org. Each org's remembered location is left
 * alone. Then drop every cached query — none of it belongs to the new org.
 */
function adoptOrgTokens(accessToken: string, refreshToken: string): void {
  useUiStore.setState({ selectedLocation: null, locations: [], locationsOrgId: null });
  setTokens(accessToken, refreshToken);
  queryClient.clear();
}

export const useAuthStore = create<AuthState>((set, get) => ({
  user: null,
  isAuthenticated: !!getTokens()?.accessToken,
  // Start at false so ProtectedRoute's effect can kick off loadUser on
  // cold loads where tokens exist but the user hasn't been hydrated yet.
  // loadUser itself flips this to true while /auth/me is in flight.
  isLoading: false,
  mfaSessionToken: null,

  login: async (email, password) => {
    const api = getApiClient();
    let data: LoginApiResponse;
    try {
      ({ data } = await api.post<LoginApiResponse>('/auth/login', { email, password }));
    } catch (err: unknown) {
      // Phase 45.1b — SSO enforcement. The backend answers 403
      // SSO_REQUIRED only AFTER verifying the password, so this is a
      // routing signal (go authenticate at the IdP), not a failure.
      const response = (
        err as {
          response?: { status?: number; data?: { code?: string; connectionId?: string } };
        }
      )?.response;
      if (
        response?.status === 403 &&
        response.data?.code === 'SSO_REQUIRED' &&
        typeof response.data.connectionId === 'string'
      ) {
        return {
          mfaRequired: false,
          ssoRequired: true,
          connectionId: response.data.connectionId,
        };
      }
      throw err;
    }

    // MFA challenge: the backend answers `{ status: 'mfa_required', mfaToken }`
    // (the RN clients' older `mfaRequired: true` flag is honoured too — see
    // `isMfaRequiredResponse`) instead of a token pair. The token pair only
    // arrives from /auth/mfa/validate.
    if (isMfaRequiredResponse(data)) {
      set({ mfaSessionToken: data.mfaToken });
      return { mfaRequired: true };
    }
    if (isMfaChallengeSignalled(data)) {
      // Defense-in-depth (mirrors rn-shared's authActions): a challenge
      // without its token is a backend regression. Fail loudly rather than
      // fall through and store `undefined` as a token pair.
      throw new Error('Server signaled MFA required but did not provide an mfaToken.');
    }
    if (typeof data.accessToken !== 'string' || typeof data.refreshToken !== 'string') {
      throw new Error('Login response did not include a token pair.');
    }

    // Normal login — store tokens and immediately fetch the full user
    // record (orgId, scoped roles, permissions). Without the loadUser
    // call here, ProtectedRoute's effect doesn't trigger one (it only
    // fires when `user` is null) and downstream code reads orgId='' —
    // which collapses POST /orgs/${orgId}/locations to /orgs/locations
    // and 404s.
    setTokens(data.accessToken, data.refreshToken);
    set({
      isAuthenticated: true,
      isLoading: false,
      mfaSessionToken: null,
    });
    await get().loadUser();
    return { mfaRequired: false };
  },

  register: async (input) => {
    const api = getApiClient();
    const { data } = await api.post('/auth/register', input);
    setTokens(data.accessToken, data.refreshToken);
    set({
      isAuthenticated: true,
      isLoading: false,
      mfaSessionToken: null,
    });
    // Same orgId-hydration rationale as login() above.
    await get().loadUser();
  },

  validateMfa: async (code) => {
    const { mfaSessionToken } = get();
    if (!mfaSessionToken) throw new Error('No MFA session');

    const api = getApiClient();
    // Same shape as a /auth/login success: token pair + user.
    const { data } = await api.post<LoginResponse>('/auth/mfa/validate', {
      mfaToken: mfaSessionToken,
      code,
    });

    setTokens(data.accessToken, data.refreshToken);
    set({
      isAuthenticated: true,
      isLoading: false,
      mfaSessionToken: null,
    });
    // Same orgId-hydration rationale as login() — fetch the full user
    // record so downstream code never reads orgId=''.
    await get().loadUser();
  },

  completeSso: async (code) => {
    // Phase 45.1b — redeem the one-time exchange code the API's SSO
    // callback appended to the /sso/callback redirect. Same token +
    // hydration flow as validateMfa: the exchange response is shaped
    // exactly like a /auth/login success.
    const api = getApiClient();
    const { data } = await api.post('/auth/sso/exchange', { token: code });

    setTokens(data.accessToken, data.refreshToken);
    set({
      isAuthenticated: true,
      isLoading: false,
      mfaSessionToken: null,
    });
    // Same orgId-hydration rationale as login() — fetch the full user
    // record so downstream code never reads orgId=''.
    await get().loadUser();
  },

  logout: async () => {
    try {
      const api = getApiClient();
      await api.post('/auth/logout');
    } catch {
      // Ignore errors during logout — clear local state regardless
    }
    clearTokens();
    set({ user: null, isAuthenticated: false, isLoading: false, mfaSessionToken: null });
    // Phase 36.4 hardening — wipe React Query cache + the billing-guard
    // overlay snapshot so the next merchant who logs in on this browser
    // can never see the previous merchant's data. Without these resets:
    //   • `useSubscription` would serve cached `['billing','subscription']`
    //     for up to 30s after sign-out (cross-tenant grace-period banner /
    //     plan name leak),
    //   • `useBillingGuardStore` would still hold the prior merchant's
    //     402 capture and the BillingGuardScreen overlay would reappear
    //     for an unrelated user.
    queryClient.clear();
    useBillingGuardStore.getState().clearGuard();
    // The remembered location is per-org, but a shared browser should
    // not carry it past sign-out either.
    clearRememberedLocations();
  },

  loadUser: async () => {
    const tokens = getTokens();
    if (!tokens?.accessToken) {
      set({ user: null, isAuthenticated: false, isLoading: false });
      return;
    }

    set({ isLoading: true });
    try {
      const api = getApiClient();
      const { data } = await api.get('/auth/me');
      set({ user: normalizeUser(data), isAuthenticated: true, isLoading: false });
    } catch {
      clearTokens();
      set({ user: null, isAuthenticated: false, isLoading: false });
    }
  },

  switchLocation: async (locationId) => {
    // Re-issue tokens scoped to this location so the JWT's `loc` claim
    // is populated. POS + floor-plan endpoints read user.loc from the
    // JWT, so without this, any location-scoped create/update fails
    // with a NOT NULL constraint error on `location_id`.
    const tokens = getTokens();
    if (!tokens?.refreshToken) return false;

    // Bump the sequence counter and capture this call's id. If a
    // newer switchLocation call starts while this one is in flight,
    // we drop the stale response on the floor instead of letting
    // out-of-order webhooks clobber the freshly-selected scope.
    const mySeq = ++switchLocationSeq;

    try {
      // `refreshTokens` (lib/axios) owns the raw-fetch call and the
      // response validation; it returns the pair without persisting it so
      // the staleness check below still runs before anything is stored.
      const data = await refreshTokens(locationId);

      // Another switchLocation call started after this one — its
      // tokens will arrive shortly, so don't overwrite with stale data.
      if (mySeq !== switchLocationSeq) return false;

      setTokens(data.accessToken, data.refreshToken);
      // Reload user so permissions + roles reflect the new scope.
      await get().loadUser();
      return true;
    } catch {
      return false;
    }
  },

  setMfaToken: (token) => set({ mfaSessionToken: token }),
  clearMfaToken: () => set({ mfaSessionToken: null }),

  enterOrg: async (orgId) => {
    const data = await platformApi.enterOrg(orgId);
    adoptOrgTokens(data.accessToken, data.refreshToken);
    await get().loadUser();
  },

  exitOrg: async () => {
    const data = await platformApi.exitOrg();
    adoptOrgTokens(data.accessToken, data.refreshToken);
    await get().loadUser();
  },

  syncOrgAfterRefresh: async () => {
    useUiStore.setState({ selectedLocation: null, locations: [], locationsOrgId: null });
    queryClient.clear();
    await get().loadUser();
  },
}));
